import PropTypes from 'prop-types'
import clsx from 'clsx'

import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
} from '@mui/material'
import useTranslations from '../../../../i18n/useTranslations'
import { getFieldProps } from '../../../../common/formProps'
import useStyles from '../styles'
import { Minus, Plus } from 'phosphor-react'
import { useCommodityTypes } from '../../../../services/commodityTypes'
import { useManufacturedCountries } from '../../../../services/manufacturedCountries'
import Autocomplete from '../../../components/autocomplete'
import TooltipButton from '../../../components/tooltipButton'

const InventoryCustom = ({
  id,
  values,
  destinCountryCode,
  originCountryCode,
  onChange,
  onBlur,
  fieldErrors,
  formErrors,
  handleAddItem,
  handleRemoveItem,
  touched,
}) => {
  const t = useTranslations()
  const classes = useStyles()
  const { data: commodityTypes } = useCommodityTypes()

  const { data: manufacturedCountries } = useManufacturedCountries()
  const truncateText = (text, maxLength) => {
    if (!text) {
      return text
    }

    if (text?.length <= maxLength) {
      return text
    } else {
      text = text.slice(0, maxLength) + '...'
      return text
    }
  }

  const renderInventory = index => {
    return (
      <>
        <Typography variant="h3" className={classes.title}>
          {t('shipping-send-form:customs:inventory-custom-number', {
            number: index + 1,
          })}
        </Typography>
        <Box className={classes.rows}>
          <Autocomplete
            className={clsx('input-medium', classes.marginInputLeft)}
            name={`inventory[${index}].category`}
            options={commodityTypes ?? []}
            inputProps={{
              error:
                touched?.wasSubmit &&
                fieldErrors?.inventory &&
                fieldErrors?.inventory[index]?.category?.key,
              helperText:
                touched?.wasSubmit &&
                fieldErrors?.inventory &&
                fieldErrors?.inventory[index]?.category?.key
                  ? t(fieldErrors?.inventory[index]?.category?.key)
                  : null,

              label: (
                <>
                  {t('shipping-send-form:customs:form:category')}
                  <TooltipButton
                    className={classes.tooltipInput}
                    type="primary"
                    title={
                      destinCountryCode !== 'ES' && originCountryCode !== 'ES'
                        ? t('tooltip:custom:category')
                        : t('tooltip:custom:category-es')
                    }
                    placement="right"
                  />
                </>
              ),
              inputProps: { className: 'input-medium' },
            }}
            InputLabelProps={{ required: true }}
            isOptionEqualToValue={(option, value) => {
              return option?.commodity_type_code === value
            }}
            getOptionLabel={option => {
              return option ? truncateText(option?.commodity_type_name, 55) : ''
            }}
            noOptionsText={t('message:no-results')}
            value={values.inventory[index]?.category}
            disableClearable={true}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option?.commodity_type_name}
              </Box>
            )}
            onChange={(_, value) => {
              if (value) {
                onChange({
                  target: {
                    name: `inventory[${index}].category`,
                    value: value,
                  },
                })
              }
            }}
            searchIcon
            autoHighlight
          />
          {destinCountryCode !== 'ES' &&
          originCountryCode !== 'ES' &&
          values?.inventory[index]?.category &&
          !values?.inventory[index]?.category?.commodity_taric_code ? (
            <TextField
              name={`inventory[${index}].category.commodity_taric_code_other`}
              label={
                <>
                  {t('shipping-send-form:customs:form:taric_code')}
                  <TooltipButton
                    className={classes.tooltipInput}
                    type="primary"
                    title={
                      <>
                        {t('shipping-send-form:customs:form:taric_code_alert')}
                        <a
                          style={{ color: 'white' }}
                          href="https://ec.europa.eu/taxation_customs/dds2/taric/taric_consultation.jsp?Lang=en#"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <strong>
                            {t(
                              'shipping-send-form:customs:form:taric_code_alert_link',
                            )}
                          </strong>
                        </a>
                      </>
                    }
                    placement="right"
                  />
                </>
              }
              InputLabelProps={{
                required:
                  destinCountryCode !== 'ES' && originCountryCode !== 'ES',
              }}
              {...getFieldProps(
                `inventory[${index}].category.commodity_taric_code_other`,
                values,
                fieldErrors,
                touched,
                t,
              )}
              onChange={onChange}
              onBlur={onBlur}
              className={clsx('input-small', classes.marginInputLeft)}
            />
          ) : null}

          <Autocomplete
            className={clsx('input-medium', classes.marginInput)}
            name={`inventory[${index}].place`}
            options={manufacturedCountries ?? []}
            inputProps={{
              error:
                touched?.wasSubmit &&
                fieldErrors?.inventory &&
                fieldErrors?.inventory[index]?.place,
              helperText:
                touched?.wasSubmit &&
                fieldErrors?.inventory &&
                fieldErrors?.inventory[index]?.place
                  ? t(fieldErrors?.inventory[index]?.place?.key)
                  : null,

              label: (
                <>
                  {t('shipping-send-form:customs:form:place')}
                  <TooltipButton
                    className={classes.tooltipInput}
                    type="primary"
                    title={t('tooltip:custom:place')}
                    placement="right"
                  />
                </>
              ),
              inputProps: { className: 'input-medium' },
            }}
            isOptionEqualToValue={(option, value) => {
              return option?.code === value
            }}
            getOptionLabel={option => {
              return option ? option?.country_ES : ''
            }}
            noOptionsText={t('message:no-results')}
            value={values.inventory[index]?.place}
            disableClearable={true}
            autoHighlight
            onChange={(_, value) => {
              if (value) {
                onChange({
                  target: { name: `inventory[${index}].place`, value: value },
                })
              }
            }}
            InputLabelProps={{ required: true }}
          />
        </Box>
        <Box component="section">
          <TextField
            multiline
            name={`inventory[${index}].description`}
            label={
              <>
                {destinCountryCode !== 'ES' && originCountryCode !== 'ES'
                  ? t('shipping-send-form:customs:form:description')
                  : t('shipping-send-form:customs:form:description-es')}
                <TooltipButton
                  className={classes.tooltipInput}
                  type="primary"
                  arrow
                  title={
                    destinCountryCode !== 'ES' && originCountryCode !== 'ES'
                      ? t('tooltip:custom:description')
                      : t('tooltip:custom:description-es')
                  }
                  placement="right"
                />
              </>
            }
            InputLabelProps={{ required: true }}
            {...getFieldProps(
              `inventory[${index}].description`,
              values,
              fieldErrors,
              touched,
              t,
            )}
            onChange={onChange}
            error={
              touched?.wasSubmit &&
              fieldErrors?.inventory &&
              fieldErrors?.inventory[index]?.description
            }
            helperText={
              touched?.wasSubmit &&
              fieldErrors?.inventory &&
              fieldErrors?.inventory[index]?.description
                ? t(fieldErrors?.inventory[index]?.description?.key)
                : null
            }
            defaultValue={values.inventory[index].description}
            value={values.inventory[index].description}
            onBlur={onBlur}
            className={clsx('input-extra-large', classes.marginInput2)}
          />
        </Box>
        <TextField
          name={`inventory[${index}].quantity`}
          label={
            <>
              {t('shipping-send-form:customs:form:quantity')}
              <TooltipButton
                className={classes.tooltipInput}
                type="primary"
                title={t('tooltip:custom:quantity')}
                placement="right"
              />
            </>
          }
          InputLabelProps={{ required: true }}
          {...getFieldProps(
            `inventory[${index}].quantity`,
            values,
            fieldErrors,
            touched,
            t,
          )}
          error={
            touched?.wasSubmit &&
            fieldErrors?.inventory &&
            fieldErrors?.inventory[index]?.quantity
          }
          helperText={
            touched?.wasSubmit &&
            fieldErrors?.inventory &&
            fieldErrors?.inventory[index]?.quantity
              ? t(fieldErrors?.inventory[index]?.quantity?.key)
              : null
          }
          onChange={onChange}
          onBlur={onBlur}
          className={clsx('input-small', classes.marginInputLeft)}
        />
        <TextField
          name={`inventory[${index}].price`}
          label={
            <>
              {t('shipping-send-form:customs:form:price')}
              <TooltipButton
                className={classes.tooltipInput}
                type="primary"
                title={t('tooltip:custom:price')}
                placement="right"
              />
            </>
          }
          InputLabelProps={{ required: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">{'€'}</InputAdornment>
            ),
          }}
          {...getFieldProps(
            `inventory[${index}].price`,
            values,
            fieldErrors,
            touched,
            t,
          )}
          error={
            touched?.wasSubmit &&
            fieldErrors?.inventory &&
            fieldErrors?.inventory[index]?.price
          }
          helperText={
            touched?.wasSubmit &&
            fieldErrors?.inventory &&
            fieldErrors?.inventory[index]?.price
              ? t(fieldErrors?.inventory[index]?.price?.key)
              : null
          }
          onChange={onChange}
          onBlur={onBlur}
          className={clsx('input-small', classes.marginInputLeft)}
        />
        {index === values.inventory.length - 1 &&
        values.inventory.length > 1 ? (
          <>
            <IconButton
              onClick={handleAddItem}
              className={classes.buttonAddRemove}
              sx={{ mt: '28px' }}
            >
              <Plus />
            </IconButton>
            <IconButton
              onClick={() => handleRemoveItem(index)}
              className={classes.buttonAddRemove}
              sx={{ mt: '27px' }}
            >
              <Minus />
            </IconButton>
          </>
        ) : values.inventory.length === 1 ? (
          <IconButton
            onClick={handleAddItem}
            className={classes.buttonAddRemove}
            sx={{ mt: '28px' }}
          >
            <Plus />
          </IconButton>
        ) : (
          <IconButton
            onClick={() => handleRemoveItem(index)}
            className={classes.buttonAddRemove}
            sx={{ mt: '27px' }}
          >
            <Minus />
          </IconButton>
        )}
      </>
    )
  }

  return (
    <>
      <Typography
        variant="h2"
        className={clsx('secondaryTitle', 'decorator', classes.title)}
      >
        {t('shipping-send-form:customs:inventory-custom')}
        <TooltipButton
          className={classes.tooltipInputTitle}
          type="primary"
          title={t('shipping-send-form:shipping-restrictions')}
          placement="right"
        />
      </Typography>

      {values.inventory.map((element, index) => {
        return renderInventory(index)
      })}
    </>
  )
}

InventoryCustom.propTypes = {
  values: PropTypes.object,
  id: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
}

export default InventoryCustom
